import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, DropdownItem, Header, Table, Icon } from 'semantic';
import { ChargingPolicy } from 'subdomains/cpo/types/charging-policy';
import Flex from 'common/layout/helpers/Flex';
import { useHistory } from 'react-router-dom';
import ConfirmDelete from 'components/ConfirmDelete';
import { request } from 'utils/api';
import { useQuery } from '@tanstack/react-query';
import { flatten, isEmpty, uniq } from 'lodash-es';
import ErrorMessage from 'components/ErrorMessage';
import { SegmentLoader } from 'subdomains/cpo/components/SegmentLoader';
import { TablePagination } from '../../../../../components/TablePagination';
import { PAGE_LIMIT } from '../../../../../config/pagination';
import { PriceAndRestrictionsLabel } from '../../../components/Tariff/PriceAndRestrictionsLabel';
import { tariffComponentKey } from './form/utils';
import {
  CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE,
  CHARGING_STATIONS_POLICIES_AND_GROUPS_NEW_CHARGING_POLICY_ROUTE,
} from 'subdomains/cpo/router/routes';
import { CHARGING_POLICIES_V1_API_URL } from 'subdomains/cpo/router/api';

export function ChargingPolicies() {
  const { t } = useTranslation();
  const historyStack = useHistory();
  const [page, setPage] = useState(1);

  const {
    data: res,
    isPending,
    error,
    refetch,
  } = useQuery<{
    data: ChargingPolicy[];
    meta: { total: number; limit: number };
  }>({
    queryKey: [CHARGING_POLICIES_V1_API_URL, page],
    queryFn: async () => {
      const { data, meta } = await request<{
        data: ChargingPolicy[];
        meta: { total: number; limit: number };
      }>({
        path: CHARGING_POLICIES_V1_API_URL,
        method: 'GET',
        params: {
          limit: PAGE_LIMIT,
          offset: (page - 1) * PAGE_LIMIT,
        },
      });

      return { data, meta };
    },
  });

  const chargingPolicies = res?.data ?? [];
  const total = res?.meta?.total ?? 0;

  const shouldRenderTable = !isPending && !error;

  return (
    <>
      <Flex direction="row" justify="space-between" align="center">
        <Header as="h2" style={{ marginTop: '1em' }}>
          {t('providerAdmin.chargingPoliciesTable.title', 'Charging Policies')}
        </Header>
        <Button
          primary
          onClick={() =>
            historyStack.push(
              CHARGING_STATIONS_POLICIES_AND_GROUPS_NEW_CHARGING_POLICY_ROUTE
            )
          }>
          <Icon name="plus" />
          {t(
            'providerAdmin.chargingPoliciesTable.newChargingPolicy',
            'New Charging Policy'
          )}
        </Button>
      </Flex>

      {isPending && <SegmentLoader />}
      <ErrorMessage error={error} />

      {shouldRenderTable && (
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t(
                  'providerAdmin.chargingPoliciesTable.columnNameAndDesc',
                  'Name & Description'
                )}
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>
                {t(
                  'providerAdmin.chargingPoliciesTable.columnCurrency',
                  'Currency'
                )}
              </Table.HeaderCell>
              <Table.HeaderCell width={4}>
                {t(
                  'providerAdmin.chargingPoliciesTable.columnPricing',
                  'Pricing'
                )}
              </Table.HeaderCell>
              <Table.HeaderCell width={3}>
                {t(
                  'providerAdmin.chargingPoliciesTable.columnAssociated',
                  'Associated'
                )}
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>
                {t('common.actions', 'Actions')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {!isPending && isEmpty(chargingPolicies) && (
              <Table.Row>
                <Table.Cell colSpan={5} style={{ textAlign: 'center' }}>
                  {t(
                    'providerAdmin.chargingPoliciesTable.noChargingPolicies',
                    'No Charging Policies'
                  )}
                </Table.Cell>
              </Table.Row>
            )}
            {chargingPolicies.map((item, index) => {
              const customerGroups = uniq(
                flatten(item.tariffRules?.map((rule) => rule.customerGroups))
              );
              const hasTariffRuleWithoutCustomerGroups = item.tariffRules?.some(
                (rule) => rule.customerGroups.length === 0
              );
              const multipleTariffAssociations =
                hasTariffRuleWithoutCustomerGroups && !isEmpty(customerGroups);

              return (
                <Table.Row key={index}>
                  <Table.Cell>
                    <strong>{item.label}</strong>
                    <br />
                    {item.description}
                  </Table.Cell>
                  <Table.Cell>{item.currency}</Table.Cell>
                  <Table.Cell>
                    {item.tariffRules?.map((rule, index) => (
                      <div key={index}>
                        {rule.tariff.components.map((component) => (
                          <PriceAndRestrictionsLabel
                            key={tariffComponentKey(component)}
                            values={component}
                            currency={item.currency}
                          />
                        ))}
                      </div>
                    ))}
                  </Table.Cell>
                  <Table.Cell>
                    {multipleTariffAssociations ? (
                      t(
                        'providerAdmin.chargingPoliciesTable.associatedWithEveryone',
                        'Some tariffs are associated with everyone, others with specific groups'
                      )
                    ) : (
                      <>
                        {isEmpty(customerGroups) &&
                          t(
                            'providerAdmin.chargingPoliciesTable.associatedWithEveryone',
                            'Associated with everyone'
                          )}

                        {!isEmpty(customerGroups) &&
                          t(
                            'providerAdmin.chargingPoliciesTable.associatedWithGroups',
                            'Associated with {{numGroups}} group(s)',
                            { numGroups: customerGroups.length }
                          )}
                      </>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Fragment>
                      <Button
                        basic
                        icon="pen-to-square"
                        title={t('common.edit', 'Edit')}
                        onClick={() =>
                          historyStack.push(
                            `${CHARGING_STATIONS_POLICIES_AND_GROUPS_CHARGING_POLICIES_ROUTE}/${item.id}/edit`
                          )
                        }
                      />

                      <Dropdown
                        icon=""
                        trigger={<Button icon="ellipsis-vertical" basic />}>
                        <Dropdown.Menu>
                          <DropdownItem>
                            <Dropdown.Item
                              content={t('common.duplicate', 'Duplicate')}
                              icon="copy"
                            />
                          </DropdownItem>
                          <DropdownItem>
                            <ConfirmDelete
                              onConfirm={async () => {
                                await request({
                                  path: `${CHARGING_POLICIES_V1_API_URL}/${item.id}`,
                                  method: 'DELETE',
                                });
                                refetch();
                              }}
                              name={item.label}
                              objectName={t(
                                'common.chargingPolicy',
                                'Charging Policy'
                              )}
                              trigger={
                                <Dropdown.Item
                                  content={t('common.delete', 'Delete')}
                                  icon="trash"
                                />
                              }
                            />
                          </DropdownItem>
                        </Dropdown.Menu>
                      </Dropdown>
                    </Fragment>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
      <TablePagination
        page={page}
        limit={PAGE_LIMIT}
        total={total}
        onPageChange={(e, data) => {
          const pageNum =
            typeof data.activePage === 'number'
              ? data.activePage
              : Number(data.activePage);
          if (!isNaN(pageNum)) {
            setPage(pageNum);
            refetch();
          }
        }}
      />
    </>
  );
}
